import { Injectable } from '@angular/core';
import { combineLatest, filter, firstValueFrom, from, map, shareReplay, startWith, switchMap } from 'rxjs';
import { CognitoService } from '../../services/cognito.service';
import { AppState } from '../../state/app.state';
import { Store } from '@ngrx/store';
import { workspaceListFeature, WorkspaceListFetch } from '@workspaces/store/workspace-list.feature';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceAuthService {
  isInited = false;
  constructor(
    private readonly cognitoService: CognitoService,
    private readonly store: Store<AppState>,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router
  ) {}

  public async init() {
    if (!this.isInited) {
      const isAdmin = await this.cognitoService.isAdmin();
      this.store.dispatch(WorkspaceListFetch({ include_archived: true, include_deleted: isAdmin }));
      this.isInited = true;
    }
  }

  public isDriftAdmin() {
    return this.cognitoService.isAdmin();
  }
  public isDriftAdmin$() {
    return from(this.isDriftAdmin());
  }

  public async isWorkspaceAdmin() {
    return firstValueFrom(this.isWorkspaceAdmin$());
  }

  public isWorkspaceAdmin$() {
    return this.getWorkspaceStatusWithUser$().pipe(map(userInWorkspace => userInWorkspace?.user?.role === 'admin'));
  }

  public getWorkspaceStatusWithUser$() {
    return combineLatest([
      this.store.select(workspaceListFeature.selectData).pipe(startWith([])),
      this.store.select(workspaceListFeature.workspaceListsIsLoading).pipe(filter(isLoading => !isLoading))
    ]).pipe(
      switchMap(([workspaceData]) => {
        const userIdPromise = this.cognitoService.getUserId();

        return from(userIdPromise).pipe(
          map(userId => ({
            userId: userId,
            workspaceData: workspaceData
          }))
        );
      }),
      map(data => {
        const currentWorkspaceId = this.getParamFromCurrentRoute('workspaceId');
        const currentWorkSpace = data.workspaceData.find(workspace => {
          return workspace.id === currentWorkspaceId;
        });
        if (!currentWorkSpace) {
          return undefined;
        }
        const userInWorkspace = currentWorkSpace.members.find(
          member => member.id === data.userId || member.cognito_id === data.userId
        );
        return { user: userInWorkspace, workspaceStatus: currentWorkSpace.state };
      })
    );
  }
  public getUserRoleWithWorkspaceStatus$() {
    return this.getWorkspaceStatusWithUser$().pipe(
      map(info => {
        return { role: info?.user?.role, status: info?.workspaceStatus };
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getParamFromCurrentRoute(key: string): string | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (route) {
      if (route.params && route.params[key]) {
        return route.params[key];
      }
      if (route.firstChild) {
        route = route.firstChild;
      } else {
        route = null;
      }
    }

    return null;
  }

  public async isWorkspaceActivated() {
    return environment.isWorkspaceActivated;
  }

  public isWorkspaceActivated$() {
    return from(this.isWorkspaceActivated());
  }
}
